import { useAuth0 } from '@auth0/auth0-react'
import { Box, Typography } from '@mui/material'
import { IconAdjustments, IconCamera } from '@tabler/icons-react'
import React, { useEffect, useRef, useState } from 'react'
import NoResultsFound from '../../../../components/EmptyScreen'
import MenuScanCoachingSheet, {
  useMenuScanCoaching,
} from '../../../../components/MenuScanCoachingSheet'
import Filter from '../../../../components/filter/Filter'
import RestaurantDishesCard from '../../../../components/restaurant/RestaurantDishesCard'
import RestaurantDrawer from '../../../../components/restaurant/RestaurantDrawer'
import RoundedButton from '../../../../components/ui/RoundedButton'
import { dietaryPreferences } from '../../../../helpers/onboard-helper'
import useAppUtilities from '../../../../hooks/useAppUtilities'
import { useAppSelector } from '../../../../store'
import {
  DishesTabFilterButton,
  DishesTabInnerContainer,
  DishesTabInnerGrid,
  DishesTabInnerGridSearchDish,
  DishesTabInputComponent,
  DishesTabMainContainer,
  DishesTabMainGrid,
  DishesTabSearchWrapper,
  DishesTabTypography,
} from '../styled/DishesTab.styled'
import {
  selectEaterDishReviews,
  setEaterDishReviews,
} from '../../../../store/profileSlice'
import {
  fetchRestaurantReviewsByAlias,
  saveRestaurantDish,
  selectMenuNameList,
  selectRestaurantDetail,
  selectRestaurantDishList,
} from '../../../../store/restaurantSlice'
import { DiningReview, IFilterResponseItem, IFilterTabList } from '../../../../types'
import { PredictedDish } from '../../../../types/menuTypes'
import { useParams } from 'react-router'
import ImpressionTracker from '../../../../components/analytics/ImpressionTracker'
import { logEvent } from '../../../../analytics/analytics'

export interface IRestaurantDishRatingReview {
  id?: string
  starCount?: number
}

interface DishesTabProps {
  showMatchText?: boolean
  onFileSelected?: (file: File, uploadMethod?: 'camera' | 'file_picker') => void
  disabled?: boolean
}

const DishesTab: React.FC<DishesTabProps> = ({ showMatchText = true, onFileSelected, disabled }) => {
  const { isAuthenticated } = useAuth0()
  const { theme, isMediumScreen, dispatch } = useAppUtilities()
  const { alias } = useParams()
  const restaurant = useAppSelector(selectRestaurantDetail)
  const menuNameList = useAppSelector(selectMenuNameList)
  const restaurantDishList = useAppSelector(selectRestaurantDishList)
  const saveRestaurantDishList = useAppSelector(selectEaterDishReviews)
  const [dishSearch, setDishSearch] = useState('')
  const [allDishes, setAllDishes] = useState<PredictedDish[]>([])
  const [searchDish, setSearchDish] = useState<PredictedDish[]>([])
  const [dishLoader, setDishLoader] = useState<boolean>(false)
  const [selectedDishRating, setSelectedDishRating] =
    useState<IRestaurantDishRatingReview>({})
  const [openDrawer, setOpenDrawer] = useState<boolean>(false)
  const [dishesData, setDishesData] = useState<Partial<PredictedDish>>({})
  const [filterTabList, setFilterTabList] = useState<IFilterTabList[]>([])
  const [filterModalOpen, setFilterModalOpen] = React.useState(false)
  const [loginFilterDish, setLoginFilterDish] = useState<PredictedDish[]>([])

  // Pre-capture coaching for the empty-menu ("No dishes yet") scan launcher — the same
  // "photograph the menu" cue the other scan entry points use, so this one no longer opens the
  // camera cold (the empty-menu page is a prime spot for it).
  const { requestCapture, coachingProps } = useMenuScanCoaching(
    'restaurant_detail',
    'restaurant_detail',
  )
  const scanInputRef = useRef<HTMLInputElement>(null)

  const menuItemList = menuNameList.map((item, index) => {
    return { key: index + 1, name: item, value: item }
  })

  const onFilterItems = (): IFilterTabList[] => {
    const filterItems = [
      menuItemList.length > 1
        ? {
            key: 1,
            name: 'Menu',
            items: menuItemList,
            filterType: 'checkbox',
          }
        : null,
      {
        key: 3,
        name: 'Dietary',
        items: dietaryPreferences.map((item, index) => {
          return { key: index + 1, name: item, value: item }
        }),
        filterType: 'checkbox',
      },
    ].filter(Boolean) as IFilterTabList[]

    if (restaurantDishList && restaurantDishList.length !== 0) {
      const prices = restaurantDishList
        .map((item) => item?.dish?.price)
        .filter((price) => typeof price === 'number')

      if (prices.length !== 0) {
        const highestPrice = Math.max(...prices)

        const priceStep = Math.ceil(highestPrice / 7)

        const data = Array.from({ length: 7 }, (v, i) => {
          const upperBound = (i + 1) * priceStep

          return {
            key: i + 1,
            name: `${upperBound - 1}`,
            value: `${upperBound - 1}`,
          }
        })

        filterItems.push({
          key: 5,
          name: 'Price',
          items: data,
          rangeStep: priceStep,
          filterType: 'range',
          priceDetail: true,
        })
      }
    }

    return filterItems
  }

  const showDetails = (dishes): void => {
    setDishesData(dishes)
    setOpenDrawer(true)
  }

  const onRating = async (
    rating: IRestaurantDishRatingReview,
  ): Promise<void> => {
    setDishLoader(true)
    setSelectedDishRating(rating)
    const payload = {
      alias: alias ?? '',
      dishId: rating.id ?? '',
      starCount: rating.starCount ?? 0,
    }
    const result = await dispatch(saveRestaurantDish(payload))

    if (saveRestaurantDish.fulfilled.match(result)) {
      const dr = result.payload
      
      // Update profile slice immediately - remove existing entry for this dishId and add new one
      const existing = saveRestaurantDishList ?? []
      // Filter out existing review for this dishId (check both dishId and dish?.dishID for compatibility)
      const filtered = existing.filter(
        (r) =>
          r.dishId !== dr.dishId &&
          (r as any).dish?.dishID !== dr.dishId,
      )
      
      // Convert DiningReviewResponse to DiningReview format
      const diningReview: DiningReview = {
        diningReviewId: dr.diningReviewId,
        restaurantAlias: dr.restaurantAlias,
        dishId: dr.dishId,
        value: dr.value ?? '',
        sentimentLabel: dr.sentimentLabel ?? '',
        sentimentScore: dr.sentimentScore ?? 0,
        sentimentSummary: dr.sentimentSummary ?? '',
        sentimentTags: dr.sentimentTags ?? '',
        name: dr.name ?? '',
        email: dr.email ?? '',
        location: dr.location ?? '',
        menuId: dr.menuId ?? '',
        created: dr.created ?? new Date().toISOString(),
        updated: dr.updated ?? new Date().toISOString(),
        starCount: dr.starCount,
      }
      
      // Update eaterDishReviews immediately so Reviewed Dishes updates instantly
      dispatch(setEaterDishReviews([...filtered, diningReview]))
      
      // Optional: background refresh to get enriched data (sentiment, images, etc.)
      if (alias) {
        // Dispatch in background (don't await) to enrich dish reviews over time
        void dispatch(fetchRestaurantReviewsByAlias({ alias }))
      }
    }

    setDishLoader(false)
  }

  // Merge restaurant dishes with eater's saved reviews to show existing ratings
  useEffect(() => {
    if (!restaurantDishList?.length) {
      setAllDishes([])
      setLoginFilterDish([])
      return
    }

    // Merge eater's dish reviews into the base dish list
    const merged = restaurantDishList.map((predDish) => {
      const review = saveRestaurantDishList?.find(
        (r) =>
          r.dishId === predDish.dish.dishID ||
          (r as any).dish?.dishID === predDish.dish.dishID,
      )

      if (!review) {
        // No user-specific review: return base dish
        return predDish
      }

      return {
        ...predDish,
        dish: {
          ...predDish.dish,
          starCount: review.starCount ?? predDish.dish.starCount ?? 0,
        },
      }
    })

    setAllDishes(merged)
    setLoginFilterDish(merged)
  }, [restaurantDishList, saveRestaurantDishList])

  useEffect(() => {
    const base = loginFilterDish ?? []
    const searchDishList =
      dishSearch.length > 0
        ? base.filter((d) =>
            d.dish.name.toLowerCase().includes(dishSearch.toLowerCase()),
          )
        : base
    setSearchDish(searchDishList)
  }, [dishSearch, isAuthenticated, loginFilterDish])

  // Menu-search intent: emit one filter_applied per settled non-empty dish query (debounced, deduped
  // per query) — the within-restaurant "what do they look for on the menu" signal, distinct from the
  // filter modal (which the shared Filter component already instruments).
  const lastDishSearchEmitRef = useRef('')
  useEffect(() => {
    const q = dishSearch.trim()
    if (!q) return
    const handle = setTimeout(() => {
      const key = q.toLowerCase()
      if (lastDishSearchEmitRef.current === key) return
      lastDishSearchEmitRef.current = key
      logEvent('filter_applied', {
        filter_name: 'dish_search',
        filter_value: q,
        screen_name: 'restaurant_detail',
      })
    }, 700)
    return () => clearTimeout(handle)
  }, [dishSearch])

  useEffect(() => {
    onFilter({})
    setFilterTabList(onFilterItems())
  }, [restaurant?.restaurantDTO?.menus, allDishes])

  const onFilter = (obj: IFilterResponseItem): void => {
    // Use merged dishes if available, otherwise fall back to restaurantDishList
    const baseDishes = allDishes.length > 0 ? allDishes : restaurantDishList ?? []
    
    if (baseDishes.length > 0) {
      const priceStep = filterTabList.find((item) => item.name === 'Price')
        ?.items[0].value

      const data = baseDishes.filter((dish) => {
        if (
          obj.price !== undefined &&
          Number(priceStep) &&
          obj.price > Number(priceStep) &&
          Number(dish.dish.price ?? 0) > Number(obj.price)
        ) {
          return false
        }

        if (obj.dietary && obj.dietary.length > 0) {
          const dietaryValues = obj.dietary.map((item) => item.name)
          if (
            !dietaryValues.some((dietary) =>
              dish?.dish?.dietary
                ?.toLocaleLowerCase()
                .split(', ')
                .includes(dietary.toLowerCase()),
            )
          ) {
            return false
          }
        }
        if (obj.menu && obj.menu.length > 0) {
          const dietaryValues = obj.menu.map((item) => item.name)
          if (
            !dietaryValues.some(
              (menu) =>
                dish.menuName?.toLocaleLowerCase() === menu.toLowerCase(),
            )
          ) {
            return false
          }
        }

        return true
      })
      setLoginFilterDish(data)
    }
  }

  return (
    <>
      {!restaurantDishList?.length && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: '56px',
            px: '24px',
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              width: '56px',
              height: '56px',
              borderRadius: '14px',
              backgroundColor: theme.palette.primary.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: '16px',
            }}
          >
            <IconCamera
              size={26}
              color={theme.palette.primary.contrastText}
              strokeWidth={1.8}
            />
          </Box>
          <Typography
            variant="h4"
            sx={{
              color: theme.palette.text.primary,
              mb: '8px',
            }}
          >
            No dishes yet
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              mb: '4px',
            }}
          >
            Be the first to see what to order here.
          </Typography>
          <Typography
            variant="overline"
            sx={{
              color: theme.palette.text.secondary,
              mb: '24px',
            }}
          >
            Points the moment you add it
          </Typography>
          <Box
            onClick={() => {
              if (disabled) return
              // Show the "photograph a menu" coaching first; open the picker on confirm
              // (runs inside the confirm-tap gesture so the native camera still opens).
              requestCapture(() => scanInputRef.current?.click())
            }}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              px: '20px',
              minHeight: '48px',
              borderRadius: '10px',
              backgroundColor: theme.palette.primary.main,
              opacity: disabled ? 0.5 : 1,
              color: theme.palette.primary.contrastText,
              fontFamily: theme.typography.button.fontFamily,
              fontWeight: 600,
              fontSize: '15px',
              cursor: disabled ? 'not-allowed' : 'pointer',
              transition: 'background-color 180ms',
              userSelect: 'none',
              '&:hover': {
                backgroundColor: disabled
                  ? theme.palette.primary.main
                  : theme.palette.primary.dark,
              },
            }}
          >
            <IconCamera
              size={16}
              color={theme.palette.primary.contrastText}
              strokeWidth={2}
            />
            See what to order
            <input
              ref={scanInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              disabled={disabled}
              onChange={(e) => {
                if (e.target.files?.[0]) {
                  onFileSelected?.(e.target.files[0], 'file_picker')
                  e.target.value = ''
                }
              }}
            />
          </Box>
        </Box>
      )}
      {Boolean(restaurantDishList?.length) && (
        <DishesTabMainContainer>
          <DishesTabInnerContainer>
            <DishesTabSearchWrapper>
              <DishesTabInputComponent
                id="dishSearch"
                value={dishSearch}
                setValue={setDishSearch}
                placeholder="Search dish name"
              />
            </DishesTabSearchWrapper>
            <DishesTabSearchWrapper>
              {isMediumScreen ? (
                <RoundedButton
                  component="p"
                  borderColor={theme.palette.grey[800]}
                  diameter={37}
                  onClick={() => setFilterModalOpen(true)}
                >
                  <IconAdjustments
                    size={18}
                    color={theme.palette.grey[800]}
                    stroke={2}
                  />
                </RoundedButton>
              ) : (
                <>
                  <Box>
                    <Filter
                      onFilterData={onFilter}
                      tabList={filterTabList}
                      isFilterModalOpen={filterModalOpen}
                      setFilterModalOpen={setFilterModalOpen}
                    />
                  </Box>
                  <DishesTabFilterButton
                    startIcon={<IconAdjustments size={18} stroke={2} />}
                    onClick={() => setFilterModalOpen(true)}
                  >
                    Filter
                  </DishesTabFilterButton>
                </>
              )}
            </DishesTabSearchWrapper>
          </DishesTabInnerContainer>
          {isMediumScreen && (
            <Box mt="12px">
              <Filter
                onFilterData={onFilter}
                tabList={filterTabList}
                isFilterModalOpen={filterModalOpen}
                setFilterModalOpen={setFilterModalOpen}
              />
            </Box>
          )}
          <DishesTabMainGrid rowSpacing={isMediumScreen ? 0 : 5}>
            {searchDish.map((item, index) => (
              <DishesTabInnerGridSearchDish key={index}>
                <ImpressionTracker
                  onImpress={() =>
                    logEvent('dish_impression', {
                      restaurant_id:
                        restaurant?.restaurantDTO?.restaurantId ??
                        restaurant?.restaurantDTO?.alias ??
                        '',
                      dish_id: item.dish.dishID,
                      rank_position: index,
                      screen_name: 'restaurant_detail',
                    })
                  }
                >
                <div onClick={() => showDetails(item)}>
                  <RestaurantDishesCard
                    isLoading={
                      dishLoader && selectedDishRating.id === item.dish.dishID
                    }
                    id={item.dish.dishID}
                    key={index}
                    image={
                      item?.dish?.imageUrl && item.dish.imageUrl.length !== 0
                        ? item.dish.imageUrl
                        : '/logoLetter-resize.svg'
                    }
                    title={item?.dish?.name}
                    description={item?.dish?.description}
                    price={item?.dish?.price}
                    onChangeRating={(rating) => {
                      void onRating(rating)
                    }}
                    isEditable
                    rating={item?.dish?.starCount ?? 0}
                    tagChipData={[
                      {
                        percentage: `${item?.probability?.yes.toFixed(2)}`,
                        title: 'Match',
                      },
                    ]}
                    showMatchText={showMatchText}
                  />
                </div>
                </ImpressionTracker>
              </DishesTabInnerGridSearchDish>
            ))}
            {!searchDish.length && (
              <DishesTabInnerGrid>
                <DishesTabTypography>
                  <NoResultsFound
                    containerHeight="400px"
                    mainText="Oops! No Results Found"
                  />
                </DishesTabTypography>
              </DishesTabInnerGrid>
            )}
          </DishesTabMainGrid>
          {dishesData?.dish?.dishID && openDrawer  && (
            <RestaurantDrawer
              open={openDrawer}
              toggleDrawer={setOpenDrawer}
              data={dishesData}
              restaurantAlias={restaurant?.restaurantDTO?.alias??''}
              restaurantId={restaurant?.restaurantDTO?.restaurantId}
            />
          )}
        </DishesTabMainContainer>
      )}
      <MenuScanCoachingSheet {...coachingProps} />
    </>
  )
}

export default DishesTab
